import * as React from "react";
import {withRouter} from "react-router-dom";
import {RouteComponentProps} from "react-router";
import {none, option, Option} from "ts-option";
import {connect, Dispatch} from "react-redux";
import {RootState} from "../../../store/reducer/root/RootState";
import Routes from "../../../navigation/route/Route";
import {api} from "../../../api/api";
import Invoice from "../../../model/invoice/Invoice";
import InvoiceDetailsView from "./InvoiceDetailsView";
import Lead from "../../../model/lead/Lead";
import Job from "../../../model/job/Job";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import Loading from "../../ui/loading/Loading";
import {OnFailure, OnSuccess, OnSuccessData} from "../../../store/action/actionHelpers";
import {fetchPartnerBrandAccounts} from "../../../store/action/partnerBrand/partnerBrand";
import {getIndividualContact} from "../../../store/view/contact";
import {InvoiceEventType} from "../../../model/invoice/event/InvoiceEvent";
import AccountingNote from "../../../model/invoice/note/AccountingNote";
import Payment from "../../../model/payment/Payment";
import IndividualContact from "../../../model/contact/IndividualContact";
import PaymentRecommendation from "../../../model/payment/PaymentRecommendation";
import {getJobByIdOrRefNumber} from "../../../store/view/job";
import {getInvoiceByJobId} from "../../../store/view/invoice";
import {fetchInvoice, fetchInvoiceEvents, fetchInvoicePayments} from "../../../store/action/invoice/invoice";
import {fetchJobByRefNumber} from "../../../store/action/job/job";
import InvoicePartsAndLabourDetails from "../../../model/invoice/InvoicePartsAndLabourDetails";
import {getLeadByIdOrRefNo} from "../../../store/view/lead";
import {fetchLead} from "../../../store/action/lead/lead";
import Account from "../../../model/account/Account";
import Bill from "../../../model/bill/Bill";
import {fetchBillByJobId} from "../../../store/action/bill/bill";
import InvoicePaymentReleaseData from "../../../model/invoice/InvoicePaymentReleaseData";
import {fetchInvoiceNotes} from "../../../store/action/note/notes";
import Toast from "../../helper/Toast";
import {NotificationTypeEnum} from "../../../model/notification/NotificationBase";
import PaymentArrivedNotification from "../../../model/notification/payment/PaymentArrivedNotification";
import InvoicePaidNotification from "../../../model/notification/invoice/InvoicePaidNotification";

interface RouteProps {
	/**
	 * Job reference number.
	 */
	jobReferenceNumber: string;
}

interface ReduxProps {
	/**
	 * Lead related to invoice.
	 */
	lead: Option<Lead>
	/**
	 * Job related to invoice.
	 */
	job: Option<Job>
	/**
	 * Bill related to invoice.
	 */
	bill: Option<Bill>;
	/**
	 * Accounts.
	 */
	accounts: Option<Account[]>;
	/**
	 * Individual contact
	 */
	contact: Option<IndividualContact>
	/**
	 * Invoice
	 */
	invoice: Option<Invoice>;
	/**
	 * Invoice notes.
	 */
	notes: Option<AccountingNote[]>;
	/**
	 * Invoice events.
	 */
	events: Option<InvoiceEventType[]>;
	/**
	 * Invoice payments.
	 */
	payments: Option<Payment[]>;
	/**
	 * Bom related to job existence indicator.
	 */
	bomExists: boolean;
	/**
	 * Last payment notification id
	 */
	lastPaymentNotificationId: Option<string>
}

interface DispatchProps {
	/**
	 * Fetch job
	 * @param {string} jobRefNumber
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchJobByRefNumber: (jobRefNumber: string, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Fetch lead.
	 * @param {string} leadId
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchLead: (leadId: string, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Fetch accounts
	 * @param {string} partnerBrandId
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchAccounts: (partnerBrandId: string, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Fetch invoice action.
	 * @param {string} jobId job identifier.
	 * @param {OnSuccessData<Invoice>} onSuccess callback.
	 */
	fetchInvoice: (jobId: string, onSuccess: OnSuccessData<Invoice>) => void;
	/**
	 * Fetch bill by job id action.
	 */
	fetchBill: (jobId: string) => void;
	/**
	 * Fetch invoice notes action.
	 * @param invoiceId
	 */
	fetchInvoiceNotes: (invoiceId: string) => void;
	/**
	 * Fetch invoice history.
	 * @param {string} invoiceId invoice identifier.
	 */
	fetchInvoiceHistory: (invoiceId: string) => void;
	/**
	 * Fetch invoice payments.
	 * @param invoiceId
	 * @param onSuccess
	 */
	fetchInvoicePayments: (invoiceId: string, onSuccess: OnSuccess, onFailure: OnFailure) => void;
}

interface State {
	/**
	 * Labour and parts details.
	 */
	labourAndPartsDetails: Option<InvoicePartsAndLabourDetails>;
	/**
	 * Invoice payment release data.
	 */
	invoicePaymentReleaseData: Option<InvoicePaymentReleaseData>;
	/**
	 * Payment recommendations.
	 */
	paymentRecommendations: Option<PaymentRecommendation[]>;
	/**
	 * Job fetched
	 */
	jobFetched: boolean;
	/**
	 * Lead fetched
	 */
	leadFetched: boolean;
}

type Props = ReduxProps & DispatchProps & RouteComponentProps<RouteProps>;

/**
 * Invoice details view.
 */
class InvoiceDetails extends React.PureComponent <Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			labourAndPartsDetails: none,
			jobFetched: false,
			leadFetched: false,
			paymentRecommendations: none,
			invoicePaymentReleaseData: none
		};
		this.fetchData = this.fetchData.bind(this);
		this.navigateToJobsList = this.navigateToJobsList.bind(this);
		this.checkJobExists = this.checkJobExists.bind(this);
		this.checkLeadExists = this.checkLeadExists.bind(this);
		this.checkBillExists = this.checkBillExists.bind(this);
	}

	componentDidMount(): void {
		this.checkJobExists();
		this.checkLeadExists();
		this.props.job.map(j => this.checkBillExists(j.id));
		this.fetchData(this.props.job);
	}

	componentWillReceiveProps(nextProps: Props) {
		this.checkJobExists(nextProps.job);
		if (this.props.job.isEmpty && nextProps.job.isDefined) {
			this.checkLeadExists(nextProps.job);
			nextProps.job.map(j => this.checkBillExists(j.id));
			this.fetchData(nextProps.job);
		}
		if (this.props.lastPaymentNotificationId.orNull !== nextProps.lastPaymentNotificationId.orNull) {
			nextProps.job.map(j => nextProps.invoice.map(() => this.props.fetchInvoice(j.id, () => {})))
			this.fetchData(nextProps.job)
		}
	}

	render() {
		return (
			<Loading isLoading={this.props.invoice.isEmpty || this.props.lead.isEmpty}>
				<InvoiceDetailsView invoice={this.props.invoice}
									job={this.props.job}
									lead={this.props.lead}
									redirectToLead={this.redirectToLead}
									redirectToJob={this.redirectToJob}
									redirectToBill={this.redirectToBill}
									viewBillVisible={this.props.bill.map(b => b.hasValue()).getOrElse(false)}
									labourAndPartsDetails={this.state.labourAndPartsDetails}
									invoicePaymentReleaseData={this.state.invoicePaymentReleaseData}
									events={this.props.events.getOrElse([])}
									notes={this.props.notes.getOrElse([])}
									payments={this.props.payments.getOrElse([])}
									paymentRecommendations={this.state.paymentRecommendations.getOrElse([])}
									accounts={this.props.accounts}
									activeNoteId={this.props.location.state ?
										this.props.location.state.activeId : ""}
									onUpdateInvoiceSuccess={this.onUpdateInvoiceSuccess}
									onSendInvoiceSuccess={this.onInvoiceSentSuccess}
									onPaymentStatusChangeSuccess={this.onPaymentStatusChangeSuccess}
									onFullPaymentConfirmation={this.onFullPaymentConfirmation}
									onSuccessfulPaymentRelease={this.onSuccessfulPaymentRelease}
									contact={this.props.contact}/>
			</Loading>
		);
	}

	/**
	 * If no job try fetch job again. If there is still no job, redirect to the jobs list
	 */
	checkJobExists(job = this.props.job) {
		if (job.isEmpty) {
			if (!this.state.jobFetched) {
				this.props.fetchJobByRefNumber(
					this.props.match.params.jobReferenceNumber,
					() => this.setState({jobFetched: true}),
					(err) => {
						notifyBugsnagAndShowToast(err, "Fetch job");
						this.navigateToJobsList();
					}
				)
			}
		}
	}

	/**
	 * If no lead, try to fetch lead again.
	 */
	checkLeadExists(job = this.props.job) {
		if (this.props.lead.isEmpty) {
			if (!this.state.leadFetched) {
				job.map(j => j.leadId.map(lId => this.props.fetchLead(
					lId,
					() => this.setState({leadFetched: true}),
					(err) => {
						notifyBugsnagAndShowToast(err, "Fetch lead");
						this.navigateToJobsList();
					}))
				);
			}
		}
	}

	/**
	 * Check if bill exists.
	 * @param props
	 * @param {string} jobId
	 */
	checkBillExists(jobId: string, props: Props = this.props) {
		if (props.bill.isEmpty) {
			this.props.fetchBill(jobId);
		}
	}

	/**
	 * Navigate to job list
	 */
	navigateToJobsList() {
		this.props.history.push(Routes.JobsList);
	}

	fetchData(job: Option<Job>) {
		job.map(j => {
			if (this.props.invoice.isDefined) {
				this.fetchInvoiceData(this.props.invoice.get)
			}
			else {
				this.props.fetchInvoice(j.id, (invoice: Invoice) => {
					this.fetchInvoiceData(invoice)
				})
			}
		});
	};

	fetchInvoiceData = (invoice: Invoice) => {
		this.fetchActivityStreamData(invoice.id);
		if (!this.props.accounts.getOrElse([]).length) {
			this.props.fetchAccounts(invoice.partnerBrandId, () => {},
				(error) => notifyBugsnagAndShowToast(error, "Fetch accounts"));
		}
		this.fetchPaymentRecommendations(invoice.id);
		this.fetchInvoicePartsAndLabourData(invoice.id);
		this.fetchInvoiceReleasePaymentData(invoice.id);
	};

	fetchInvoicePartsAndLabourData = (invoiceId: string) => {
		return api.instance.invoiceApi.fetchInvoicePartsAndLabourDetails(invoiceId)
			.then(details => this.setState({labourAndPartsDetails: option(details)}))
			.catch(error => notifyBugsnagAndShowToast(error, "Fetch invoice parts and labour details"))
	};

	fetchInvoiceReleasePaymentData = (invoiceId: string) =>
		api.instance.invoiceApi.fetchPaymentReleaseData(invoiceId)
			.then((data) => this.setState({invoicePaymentReleaseData: option(data)}))
			.catch((error) => notifyBugsnagAndShowToast(error, "Fetch invoice payment release data"));

	fetchActivityStreamData = (invoiceId: string) => {
		this.props.fetchInvoiceHistory(invoiceId);
		this.props.fetchInvoiceNotes(invoiceId);
		this.props.fetchInvoicePayments(invoiceId, () => {}, () => {});
	};

	onUpdateInvoiceSuccess = (invoice: Invoice) => {
		this.props.fetchInvoiceHistory(invoice.id);
		this.props.fetchInvoiceNotes(invoice.id);
		this.fetchInvoicePartsAndLabourData(invoice.id);
		this.fetchInvoiceReleasePaymentData(invoice.id);
	};

	onInvoiceSentSuccess = () => {
		this.props.invoice.map(inv => this.props.fetchInvoiceHistory(inv.id));
		this.props.job.map(j => this.props.fetchInvoice(j.id, () => {}));
	};

	onPaymentStatusChangeSuccess = (onSuccess: OnSuccess) => {
		this.props.job.map(j => {
			this.props.fetchInvoice(j.id, (invoice: Invoice) =>
				Promise.all([
					new Promise((resolve, reject) => this.props.fetchInvoicePayments(invoice.id, resolve, reject)),
					this.fetchPaymentRecommendations(invoice.id)
				]).then(onSuccess)
			);
		});
	};

	onFullPaymentConfirmation = (onSuccess: OnSuccess) => {
		this.props.job.map(j => {
			this.props.fetchInvoice(j.id, (invoice: Invoice) => {
				this.props.fetchInvoiceHistory(invoice.id);
				onSuccess();
			});
		});
	};

	onSuccessfulPaymentRelease = () => {
		this.props.job.map(j => {
			this.props.fetchInvoice(j.id, (inv) => {
				this.fetchInvoiceReleasePaymentData(inv.id);
				this.props.fetchJobByRefNumber(j.referenceNumber,
					() => {},
					(error) => {notifyBugsnagAndShowToast(error, "Fetch job")});
			});
		});
	};

	redirectToJob = () => this.props.history.push(Routes.EditJob(this.props.match.params.jobReferenceNumber));

	redirectToLead = () => this.props.lead.map(l => this.props.history.push(Routes.EditLead(l.referenceNumber)));

	redirectToBill = () => this.props.bill.map(b => this.props.history.push(Routes.BillDetailsView(b.id)));

	fetchPaymentRecommendations = (invoiceId: string) => {
		return api.instance.invoicePaymentApi.fetchInvoicePaymentRecommendations(invoiceId)
			.then(recommendations => this.setState({paymentRecommendations: option(recommendations)}))
			.catch(err => notifyBugsnagAndShowToast(err, "Fetch payment recommendations"));
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchJobByRefNumber: (jobRefNumber: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			fetchJobByRefNumber(jobRefNumber, onSuccess, onFailure)),
		fetchLead: (leadId: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			fetchLead(leadId, onSuccess, onFailure)),
		fetchAccounts: (partnerBrandId: string, onSuccess: OnSuccess, onFailure: OnFailure) =>
			dispatch(fetchPartnerBrandAccounts(partnerBrandId, onSuccess, onFailure)),
		fetchInvoice: (jobId: string, onSuccess: OnSuccessData<Invoice>) =>
			dispatch(fetchInvoice(jobId, onSuccess, () => {})),
		fetchBill: (jobId: string) => dispatch(fetchBillByJobId(jobId, () => {}, () => {})),
		fetchInvoiceNotes: (invoiceId: string) => dispatch(
			fetchInvoiceNotes(invoiceId, () => {}, (error => notifyBugsnagAndShowToast(error, "Fetch invoice notes")))),
		fetchInvoiceHistory: (invoiceId: string) => dispatch(fetchInvoiceEvents(invoiceId, () => {},
			() => Toast.error("Cannot fetch invoice events", "Invoice events"))),
		fetchInvoicePayments: (invoiceId: string, onSuccess: OnSuccess, onFailure: OnFailure) =>
			dispatch(fetchInvoicePayments(
				invoiceId,
				onSuccess,
				(error) => {
					Toast.error("Cannot fetch invoice payments", "Invoice payments");
					onFailure(error);
				})
			)
	}
}

function mapStateToProps(state: RootState, props: Props) {
	const job = getJobByIdOrRefNumber(props.match.params.jobReferenceNumber, state);
	const jobId = job.map(j => j.id).orNull;
	const lead = job.flatMap(j => j.leadId.flatMap(lId => getLeadByIdOrRefNo(lId, state)));
	const contactId = job.map(j => j.data.principalContactId);
	const contact = contactId.flatMap(getIndividualContact);
	let accounts = contact.flatMap(c => option(state.partnerBrand.accounts[c.data.partnerBrandId.orNull]));
	const invoice = getInvoiceByJobId(jobId, state);
	const bill = option(state.bill.billsByJobIdMap[jobId]);
	const invoiceId = invoice.map(i => i.id).orNull;
	const notes = option(state.note.invoiceNotes[invoiceId]);
	const events = option(state.invoice.events[invoiceId]);
	const payments = option(state.invoice.payments[invoiceId]);
	const lastPaymentNotificationId = option(state.notification.notifications.filter(
		n => (n.type === NotificationTypeEnum.PaymentArrived
			&& (n as PaymentArrivedNotification).data.invoiceId.orNull === invoiceId)
			|| (n.type === NotificationTypeEnum.InvoicePaid
				&& (n as InvoicePaidNotification).data.invoiceId === invoiceId))[0]).map(n => n.id)
	return {
		job,
		lead,
		accounts,
		contact,
		invoice,
		bill,
		notes,
		events,
		payments,
		lastPaymentNotificationId
	};
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InvoiceDetails))
